import React, { ReactNode } from 'react';
import { DataModeContext, useDataModeState, useDataMode } from '@/hooks/useDataMode';

interface DataModeProviderProps {
  children: ReactNode;
}

/**
 * Data Mode Provider Component
 * Wraps the application to provide data mode context throughout the component tree
 */
export const DataModeProvider: React.FC<DataModeProviderProps> = ({ children }) => {
  const dataModeState = useDataModeState();

  return (
    <DataModeContext.Provider value={dataModeState}>
      {children}
    </DataModeContext.Provider>
  );
};

/**
 * Data Mode Indicator Component
 * Shows current data mode in development or when explicitly enabled
 */
export const DataModeIndicator: React.FC = () => {
  // Use the shared context state
  const { dataMode, isDemoMode, toggleDataMode } = useDataMode();
  
  
  // Only show in development or when explicitly enabled
  const shouldShow = 
    import.meta.env.DEV;

  const allowToggle = import.meta.env.VITE_ALLOW_DATA_MODE_TOGGLE !== 'false';

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div
        className={`
          px-3 py-2 rounded-full text-xs font-medium shadow-lg border
          ${isDemoMode 
            ? 'bg-amber-100 text-amber-800 border-amber-200' 
            : 'bg-green-100 text-green-800 border-green-200'
          }
          ${allowToggle ? 'cursor-pointer hover:shadow-xl transition-shadow' : 'cursor-default'}
        `}
        onClick={allowToggle ? toggleDataMode : undefined}
        title={allowToggle ? 'Click to toggle data mode' : undefined}
      >
        <div className="flex items-center space-x-2">
          <div
            className={`
              w-2 h-2 rounded-full
              ${isDemoMode ? 'bg-amber-500' : 'bg-green-500'}
            `}
          />
          <span className="select-none">
            {isDemoMode ? 'Demo Data' : 'Live Data'}
          </span>
          {allowToggle && (
            <svg 
              className="w-3 h-3 ml-1" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" 
              />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};